import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ArrowDown, ArrowUp, ArrowUpDown, FilePlus, Loader2, PackageX, Search, Check, X } from "lucide-react";
import { apiGet, apiPost, fmtDate as formatCompactDate } from "@/utils/api";
import { toast } from "sonner";

const SAP_BLUE = "#0854A0";
const SAP_SELECTED_BG = "#DAE9F8";
const QN_REQUIRED_REASONS = new Set(["QUALITY_REJECTION", "WRONG_GRADE_CERTIFICATE"]);

function buildHaystack(item) {
  return [
    item.rts_id, item.vendor_name, item.vendor_id, item.material_code, item.material_description,
    item.quality_notification_number, item.invoice_number, item.invoice_id, item.po_number,
    item.po_item_number, item.grn_id, item.reason_label, item.site, item.credit_memo_id, item.notes,
  ].filter(Boolean).map(String).join(" \u2003 ").toLowerCase();
}

function sortValue(item, key) {
  switch (key) {
    case "rts_id":    return (item.rts_id || "").toLowerCase();
    case "vendor":    return (item.vendor_name || "").toLowerCase();
    case "material":  return (item.material_code || "").toLowerCase();
    case "quantity":  return Number(item.return_quantity || 0);
    case "reason":    return (item.reason_label || "").toLowerCase();
    case "qn":        return (item.quality_notification_number || "").toLowerCase();
    case "invoice":   return (item.invoice_number || "").toLowerCase();
    case "cm":        return (item.credit_memo_id || "").toLowerCase();
    case "created":
    default:          return item.created_at || "";
  }
}

function compareBy(key, dir) {
  const sign = dir === "desc" ? -1 : 1;
  return (a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    return 0;
  };
}

function SortableHeader({ label, sortKey: colKey, activeKey, dir, onSort, align = "left" }) {
  const active = activeKey === colKey;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return <th className={`px-4 py-3 font-semibold ${align === "right" ? "text-right" : ""}`}>
    <button type="button" onClick={() => onSort(colKey)}
      className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-900 ${active ? "text-slate-900" : "text-slate-600"}`}
      data-testid={`rts-sort-header-${colKey}`}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      {label}<Icon size={11} className={active ? "" : "opacity-40"} />
    </button>
  </th>;
}

export default function ReturnsToSupplier() {
  const [items, setItems] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [sapFollowUpOptions, setSapFollowUpOptions] = useState([]);
  const [vendorsSeen, setVendorsSeen] = useState([]);
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [linkedFilter, setLinkedFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");

  const toggleSort = useCallback((key) => {
    setSortKey((prev) => (prev === key ? prev : key));
    setSortDir((prevDir) => key === sortKey
      ? (prevDir === "asc" ? "desc" : "asc")
      : (["quantity", "created"].includes(key) ? "desc" : "asc"));
  }, [sortKey]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (reasonFilter !== "all") params.set("reason", reasonFilter);
      if (linkedFilter !== "all") params.set("linked", linkedFilter);
      const resp = await apiGet(`/returns?${params}`);
      setItems(resp.items || []);
      setReasonOptions(resp.reason_options || []);
      setSapFollowUpOptions(resp.sap_follow_up_decision_options || []);
      setVendorsSeen(resp.vendors_seen || []);
    } catch (err) {
      setError(err.message || "Could not load returns");
    } finally {
      setLoading(false);
    }
  }, [reasonFilter, linkedFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = q
      ? items.filter((item) => q.split(/\s+/).every((token) => buildHaystack(item).includes(token)))
      : items;
    return [...matched].sort(compareBy(sortKey, sortDir));
  }, [items, search, sortKey, sortDir]);

  return <section className="bg-white" data-testid="returns-to-supplier-page">
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-[#F7F7F7] px-4 py-2.5">
      <div className="flex items-baseline gap-2">
        <h2 className="text-[14px] font-semibold text-slate-900" data-testid="rts-heading">Returns to Supplier</h2>
        <span className="text-[12px] text-slate-500" data-testid="rts-total-count">({filtered.length}{items.length !== filtered.length ? ` of ${items.length}` : ""})</span>
      </div>
      <Button
        onClick={() => setShowCreate(true)}
        className="h-8 gap-1.5 text-[12px] font-medium text-white"
        style={{ backgroundColor: SAP_BLUE }}
        data-testid="create-rts-toolbar-button"
      ><FilePlus size={13} /> Create RTS</Button>
    </header>

    <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-4 py-2">
      <div className="relative min-w-[260px] flex-1">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(event) => setSearch(event.target.value)} className="h-8 border-slate-300 bg-white pl-8 text-[12px]"
          placeholder="Search RTS, vendor, material, QN#, invoice, GRN…" data-testid="rts-search-input" />
      </div>
      <Select value={reasonFilter} onValueChange={setReasonFilter}>
        <SelectTrigger className="h-8 w-[220px] border-slate-300 bg-white text-[12px]" data-testid="rts-reason-filter"><SelectValue placeholder="All reasons" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="rts-reason-filter-all">All reasons</SelectItem>
          {reasonOptions.map((option) => <SelectItem key={option.code} value={option.code} data-testid={`rts-reason-filter-${option.code}`}>{option.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="inline-flex h-8 overflow-hidden rounded border border-slate-300" role="group" data-testid="rts-linked-toggle">
        {[["all", "All"], ["yes", "Invoice-linked"], ["no", "Standalone"]].map(([key, label]) => (
          <button key={key} type="button" aria-pressed={linkedFilter === key}
            onClick={() => setLinkedFilter(key)} data-testid={`rts-linked-${key}`}
            className={`px-3 text-[12px] font-medium transition-colors ${linkedFilter === key ? "bg-slate-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{label}</button>
        ))}
      </div>
    </div>

    {error && <p role="alert" className="border-b border-red-200 bg-red-50 px-4 py-1.5 text-[11px] text-red-800" data-testid="rts-error">{error}</p>}

    {loading ? (
      <div className="flex h-28 items-center justify-center" data-testid="rts-loading"><Loader2 size={16} className="animate-spin" style={{ color: SAP_BLUE }} /></div>
    ) : filtered.length === 0 ? (
      <div className="flex min-h-24 items-center justify-center bg-white px-4 py-10 text-center text-[12px] text-slate-500" data-testid="rts-empty">
        <div>
          <PackageX size={28} className="mx-auto mb-2 text-slate-300" />
          {items.length === 0 ? "No returns have been recorded yet. Click Create RTS to record a quality rejection, damage, or over-shipment." : "No RTS entries match your filters."}
        </div>
      </div>
    ) : (
      <div className="overflow-x-auto bg-white" data-testid="rts-table">
        <table className="w-full border-collapse text-left text-[13px]">
          <thead>
            <tr className="border-b-2 border-slate-300 bg-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-700">
              <SortableHeader label="RTS ID" sortKey="rts_id" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Vendor" sortKey="vendor" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Material" sortKey="material" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Quantity" sortKey="quantity" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Reason" sortKey="reason" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="QN #" sortKey="qn" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Linked Invoice" sortKey="invoice" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Credit Memo" sortKey="cm" activeKey={sortKey} dir={sortDir} onSort={toggleSort} />
              <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-slate-700">SAP Draft</th>
              <SortableHeader label="Created" sortKey="created" activeKey={sortKey} dir={sortDir} onSort={toggleSort} align="right" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-300">
            {filtered.map((item) => (
              <tr key={item.rts_id}
                className="bg-white hover:bg-slate-50"
                data-testid={`rts-row-${item.rts_id}`}>
                <td className="px-4 py-3 font-mono text-[12px] text-slate-800" data-testid={`rts-id-${item.rts_id}`}>{item.rts_id}</td>
                <td className="px-4 py-3 text-slate-700" data-testid={`rts-vendor-${item.rts_id}`}>{item.vendor_name || item.vendor_id || "—"}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-slate-700" data-testid={`rts-material-${item.rts_id}`}>
                  <div>{item.material_code || "—"}</div>
                  {item.material_description && <div className="text-slate-500">{item.material_description}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-slate-700" data-testid={`rts-qty-${item.rts_id}`}>{item.return_quantity} {item.uom}</td>
                <td className="px-4 py-3 text-slate-700" data-testid={`rts-reason-${item.rts_id}`}>{item.reason_label}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-slate-700" data-testid={`rts-qn-${item.rts_id}`}>{item.quality_notification_number || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-[12px] text-slate-700" data-testid={`rts-invoice-${item.rts_id}`}>
                  {item.invoice_number ? <>
                    <div>{item.invoice_number}</div>
                    {item.grn_id && <div className="text-slate-500">GRN {item.grn_id}</div>}
                  </> : <span className="text-slate-400">Standalone{item.grn_id ? ` · GRN ${item.grn_id}` : ""}</span>}
                </td>
                <td className="px-4 py-3 font-mono text-[12px] text-slate-700" data-testid={`rts-cm-${item.rts_id}`}>
                  {item.credit_memo_id ? <>
                    <div>{item.credit_memo_id}</div>
                    <div className="text-slate-500">{item.credit_memo_status === "pending_sap_post" ? "Pending SAP post" : item.credit_memo_status}</div>
                  </> : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-[12px]" data-testid={`rts-sap-${item.rts_id}`}>
                  <SapDraftCell item={item} onPosted={load} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-right text-[12px] text-slate-600" title={item.created_at} data-testid={`rts-created-${item.rts_id}`}>{formatCompactDate(item.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    <CreateReturnDialog
      open={showCreate}
      onClose={() => setShowCreate(false)}
      reasonOptions={reasonOptions}
      vendorsSeen={vendorsSeen}
      sapFollowUpOptions={sapFollowUpOptions}
      onCreated={() => { setShowCreate(false); load(); }}
    />
  </section>;
}

/* ──────────────────────────────────────────────────────────────────── *
 * SapDraftCell — SAP posting state + "Post to SAP" action for one row.
 * ──────────────────────────────────────────────────────────────────── */
function SapDraftCell({ item, onPosted }) {
  const [busy, setBusy] = useState(false);
  const state = item.sap_state || "";
  const posted = state === "verified_draft" && item.sap_rts_id;

  const postToSap = async () => {
    setBusy(true);
    try {
      const resp = await apiPost(`/returns/${encodeURIComponent(item.rts_id)}/post-to-sap`, {});
      if (resp.sap_state === "verified_draft") {
        toast.success(`SAP RTS ${resp.sap_rts_id} created`);
      } else if (resp.sap_state === "dry_run") {
        toast.info("Dry-run OK · set RTS_SAP_LIVE_WRITE=true in backend/.env to post live");
      } else if (resp.sap_state === "error") {
        toast.error(resp.error || "SAP rejected the RTS");
      } else {
        toast.success(resp.already_posted ? "Already posted" : "RTS submitted");
      }
      onPosted?.();
    } catch (err) {
      toast.error(err.message || "Could not post RTS to SAP");
    } finally {
      setBusy(false);
    }
  };

  if (posted) {
    return <div className="font-mono text-[11px] text-emerald-700" data-testid={`rts-sap-posted-${item.rts_id}`}>
      <div>{item.sap_rts_id}</div>
      <div className="text-slate-500">SAP draft</div>
    </div>;
  }
  return <div className="flex flex-col items-start gap-1">
    <Button
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={postToSap}
      className="h-7 px-2 text-[11px]"
      data-testid={`rts-post-sap-${item.rts_id}`}
    >
      {busy ? <Loader2 size={11} className="animate-spin" /> : "Post to SAP"}
    </Button>
    {state === "dry_run" && <span className="text-[10px] text-amber-700">Dry-run OK</span>}
    {state === "error" && <span className="text-[10px] text-red-700" title={item.sap_last_error}>Error · retry</span>}
  </div>;
}

/* ──────────────────────────────────────────────────────────────────── *
 * CreateReturnDialog — invoice-linked only.
 *
 * Per Indian GST (CGST Act §34, Rule 53) every credit note must reference
 * an underlying tax invoice, so the dialog has a single flow:
 *   1. User types the Invoice Number + clicks Fetch.
 *   2. Backend resolves it (and disambiguates if more than one match).
 *   3. Vendor + PO + GRN + material lines auto-populate from that invoice.
 *   4. User picks a material line, enters return qty + reason, submits.
 *
 * Pre-invoice gate rejections belong to the Delivery-Challan flow (Rule
 * 55), which is out of this app's scope.
 * ──────────────────────────────────────────────────────────────────── */

export function CreateReturnDialog({ open, onClose, reasonOptions, sapFollowUpOptions = [], onCreated, prefillInvoice }) {
  const [reason, setReason] = useState("");
  const [qn, setQn] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [materialCode, setMaterialCode] = useState("");
  const [materialDescription, setMaterialDescription] = useState("");
  const [po, setPo] = useState("");
  const [poItem, setPoItem] = useState("");
  const [grnId, setGrnId] = useState("");
  const [grnItemNumber, setGrnItemNumber] = useState("");
  const [uom, setUom] = useState("");
  const [returnQty, setReturnQty] = useState("");
  const [site, setSite] = useState("");
  const [notes, setNotes] = useState("");

  // Invoice-first flow state.
  const [invoiceNumberInput, setInvoiceNumberInput] = useState("");
  const [invoiceCatalog, setInvoiceCatalog] = useState([]);
  const [chosenInvoiceId, setChosenInvoiceId] = useState("");
  const [chosenLineId, setChosenLineId] = useState("");
  const [invoiceLookupBusy, setInvoiceLookupBusy] = useState(false);
  const [invoiceLookupError, setInvoiceLookupError] = useState("");

  const [sapFollowUpCode, setSapFollowUpCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const resetForm = useCallback(() => {
    setReason(""); setQn(""); setVendorId(""); setVendorName("");
    setMaterialCode(""); setMaterialDescription(""); setPo(""); setPoItem(""); setGrnId(""); setGrnItemNumber("");
    setUom(""); setReturnQty(""); setSite(""); setNotes("");
    setInvoiceNumberInput(""); setInvoiceCatalog([]);
    setChosenInvoiceId(""); setChosenLineId(""); setInvoiceLookupError("");
    setSapFollowUpCode(""); setFormError("");
  }, []);

  useEffect(() => { if (!open) resetForm(); }, [open, resetForm]);

  const qnRequired = QN_REQUIRED_REASONS.has(reason);
  const chosenInvoice = useMemo(() => invoiceCatalog.find((inv) => inv.invoice_id === chosenInvoiceId) || null, [invoiceCatalog, chosenInvoiceId]);
  const chosenLine = useMemo(() => chosenInvoice?.returnable_lines?.find((line) => (line.line_id || "") === chosenLineId) || null, [chosenInvoice, chosenLineId]);
  const selectedReasonOption = useMemo(
    () => reasonOptions.find((option) => option.code === reason) || null,
    [reasonOptions, reason],
  );

  // Auto-stamp SAP follow-up decision from the picked reason.
  useEffect(() => {
    if (!selectedReasonOption) return;
    setSapFollowUpCode((prev) => prev || selectedReasonOption.sap_follow_up_default || "01");
  }, [selectedReasonOption]);

  const fetchInvoiceByNumber = async () => {
    const num = invoiceNumberInput.trim();
    if (!num) { setInvoiceLookupError("Enter an invoice number first"); return; }
    setInvoiceLookupBusy(true); setInvoiceLookupError(""); setFormError("");
    try {
      const resp = await apiGet(`/returns/invoice-by-number?invoice_number=${encodeURIComponent(num)}`);
      const items = resp.items || [];
      setInvoiceCatalog(items);
      if (items.length === 1) {
        const only = items[0];
        setChosenInvoiceId(only.invoice_id);
        setVendorId(only.vendor_id || "");
        setVendorName(only.vendor_name || "");
        if (only.site && !site) setSite(only.site);
      } else {
        setChosenInvoiceId("");
        setVendorId(""); setVendorName("");
      }
      setChosenLineId("");
      setMaterialCode(""); setMaterialDescription(""); setPo(""); setPoItem("");
      setGrnId(""); setGrnItemNumber(""); setUom("");
    } catch (err) {
      setInvoiceCatalog([]); setChosenInvoiceId("");
      setInvoiceLookupError(err.message || "Could not find that invoice");
      setVendorId(""); setVendorName("");
    } finally {
      setInvoiceLookupBusy(false);
    }
  };

  const pickInvoiceFromMulti = (invoiceId) => {
    setChosenInvoiceId(invoiceId);
    setChosenLineId("");
    const match = invoiceCatalog.find((inv) => inv.invoice_id === invoiceId);
    if (match) {
      setVendorId(match.vendor_id || "");
      setVendorName(match.vendor_name || "");
      if (match.site && !site) setSite(match.site);
    }
  };

  // Prefill from an invoice context (inline button on the invoice drawer).
  useEffect(() => {
    if (!open || !prefillInvoice) return;
    if (prefillInvoice.invoice_number) {
      setInvoiceNumberInput(prefillInvoice.invoice_number);
      setTimeout(() => { fetchInvoiceByNumber(); }, 0);
    }
  }, [open, prefillInvoice]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill from the chosen invoice line.
  useEffect(() => {
    if (!chosenLine) return;
    setMaterialCode(chosenLine.material_code || "");
    setMaterialDescription(chosenLine.material_description || "");
    setPo(chosenLine.po_number || "");
    setPoItem(chosenLine.po_item_number || "");
    setGrnId(chosenLine.grn_id || "");
    setGrnItemNumber(chosenLine.grn_item_number || "");
    setUom(chosenLine.uom || "");
  }, [chosenLine]);

  const submit = async () => {
    setFormError("");
    if (!chosenInvoiceId) return setFormError("Fetch an invoice first (enter the number and click Fetch)");
    if (!chosenLineId) return setFormError("Pick a material line from the invoice");
    if (!reason) return setFormError("Pick a reason");
    if (qnRequired && !qn.trim()) return setFormError(`Quality Notification number is required when the reason is ${reasonOptions.find((o) => o.code === reason)?.label || reason}`);
    if (!returnQty || Number(returnQty) <= 0) return setFormError("Return quantity must be greater than zero");
    if (chosenLine && Number(returnQty) > chosenLine.remaining_returnable_quantity) {
      return setFormError(`Return quantity ${returnQty} exceeds the remaining returnable quantity ${chosenLine.remaining_returnable_quantity} on this invoice line`);
    }

    setSubmitting(true);
    try {
      const payload = {
        reason,
        quality_notification_number: qn.trim() || null,
        return_quantity: Number(returnQty),
        uom,
        material_code: materialCode,
        material_description: materialDescription,
        vendor_id: vendorId,
        vendor_name: vendorName,
        site,
        po_number: po,
        po_item_number: poItem,
        grn_id: grnId,
        grn_item_number: grnItemNumber,
        sap_reason_code: selectedReasonOption?.sap_reason_code || null,
        sap_follow_up_decision_code: sapFollowUpCode || null,
        notes,
        invoice_id: chosenInvoice.invoice_id,
        invoice_number: chosenInvoice.invoice_number,
        invoice_line_id: chosenLine?.line_id || "",
      };
      await apiPost("/returns", payload);
      toast.success("RTS recorded · credit memo stub created");
      onCreated?.();
    } catch (err) {
      setFormError(err.message || "Could not create the RTS");
    } finally {
      setSubmitting(false);
    }
  };

  const lineLocked = Boolean(chosenLine);

  return <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
    <DialogContent className="max-w-3xl" data-testid="create-rts-dialog">
      <DialogHeader>
        <DialogTitle className="text-[15px]">Create Return to Supplier</DialogTitle>
        <DialogDescription className="text-[12px]">Returns must be linked to a booked tax invoice (GST §34). A local credit-memo stub is auto-created and flagged pending SAP post.</DialogDescription>
      </DialogHeader>

      {/* Invoice-number-first panel */}
      <div className="rounded border border-slate-200 bg-slate-50 p-3" data-testid="rts-invoice-lookup-panel">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="rts-invoice-number" className="text-[12px]">Invoice Number <span className="text-red-700">*</span></Label>
            <Input
              id="rts-invoice-number"
              value={invoiceNumberInput}
              onChange={(e) => setInvoiceNumberInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchInvoiceByNumber(); } }}
              placeholder="e.g. 729/26-27"
              disabled={invoiceLookupBusy}
              data-testid="rts-invoice-number-input"
            />
          </div>
          <Button
            type="button"
            onClick={fetchInvoiceByNumber}
            disabled={invoiceLookupBusy || !invoiceNumberInput.trim()}
            className="h-9 text-white"
            style={{ backgroundColor: SAP_BLUE }}
            data-testid="rts-invoice-fetch-button"
          >
            {invoiceLookupBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null}
            Fetch
          </Button>
        </div>
        {invoiceLookupError && <p role="alert" className="mt-2 text-[11px] text-red-800" data-testid="rts-invoice-lookup-error">{invoiceLookupError}</p>}

        {invoiceCatalog.length > 1 && (
          <div className="mt-2 space-y-1">
            <Label className="text-[12px]">Multiple invoices match — pick one</Label>
            <Select value={chosenInvoiceId} onValueChange={pickInvoiceFromMulti}>
              <SelectTrigger data-testid="rts-invoice-disambig-select"><SelectValue placeholder="Pick the right invoice" /></SelectTrigger>
              <SelectContent>
                {invoiceCatalog.map((inv) => <SelectItem key={inv.invoice_id} value={inv.invoice_id} data-testid={`rts-invoice-disambig-option-${inv.invoice_id}`}>
                  {inv.invoice_number} · {inv.vendor_name || inv.vendor_id} · {formatCompactDate(inv.invoice_date)} · ₹{(inv.grand_total || 0).toLocaleString?.() || inv.grand_total}
                </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {chosenInvoice && (
          <div className="mt-2 rounded border border-slate-200 bg-white p-2 text-[11px]" data-testid="rts-invoice-summary">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span><span className="text-slate-500">Vendor</span> <span className="font-mono text-slate-800">{chosenInvoice.vendor_id}</span> · {chosenInvoice.vendor_name || "—"}</span>
              <span><span className="text-slate-500">PO</span> <span className="font-mono">{chosenInvoice.po_number || "—"}</span></span>
              <span><span className="text-slate-500">Date</span> {formatCompactDate(chosenInvoice.invoice_date)}</span>
              <span><span className="text-slate-500">Status</span> <span className="font-mono">{chosenInvoice.status}</span></span>
            </div>
          </div>
        )}

        {chosenInvoice && (chosenInvoice.returnable_lines || []).length > 0 && (
          <div className="mt-2 space-y-1">
            <Label className="text-[12px]">Material line <span className="text-red-700">*</span></Label>
            <Select value={chosenLineId} onValueChange={setChosenLineId}>
              <SelectTrigger data-testid="rts-invoice-line-select"><SelectValue placeholder="Pick a material to return" /></SelectTrigger>
              <SelectContent>
                {chosenInvoice.returnable_lines.map((line) => <SelectItem key={line.line_id || line.material_code} value={line.line_id || ""} data-testid={`rts-invoice-line-${line.line_id}`}>
                  {line.material_code} · {line.material_description || "—"} · remaining {line.remaining_returnable_quantity} {line.uom} (billed {line.billed_quantity}, returned {line.already_returned_quantity})
                </SelectItem>)}
              </SelectContent>
            </Select>
            {chosenLine && chosenLine.remaining_returnable_quantity === 0 && <p className="text-[11px] text-amber-800">All of this line's quantity has already been returned on previous RTS entries.</p>}
          </div>
        )}
        {chosenInvoice && (chosenInvoice.returnable_lines || []).length === 0 && (
          <p className="mt-2 text-[11px] text-amber-800">This invoice has no returnable lines.</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="rts-reason">Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger id="rts-reason" data-testid="rts-reason-select"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
            <SelectContent>
              {reasonOptions.map((option) => <SelectItem key={option.code} value={option.code} data-testid={`rts-reason-option-${option.code}`}>{option.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="rts-qn">Quality Notification # {qnRequired && <span className="text-red-700">*</span>}</Label>
          <Input id="rts-qn" value={qn} onChange={(e) => setQn(e.target.value)} placeholder={qnRequired ? "Required" : "Optional"} data-testid="rts-qn-input" />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rts-return-handling">Return handling</Label>
          <Select value={sapFollowUpCode} onValueChange={setSapFollowUpCode}>
            <SelectTrigger id="rts-return-handling" data-testid="rts-sap-decision-select">
              <SelectValue placeholder="Pick how the return is settled" />
            </SelectTrigger>
            <SelectContent>
              {sapFollowUpOptions.map((option) => (
                <SelectItem key={option.code} value={option.code} data-testid={`rts-sap-decision-option-${option.code}`}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          {/* Spacer to keep the grid aligned (reason + handling on row 1). */}
        </div>

        <div className="space-y-1 md:col-span-2">
          <Label>Vendor <span className="text-[10px] text-slate-500">(from invoice)</span></Label>
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[12px]" data-testid="rts-vendor-locked">
            {vendorId ? <>
              <span className="font-mono text-slate-800">{vendorId}</span>
              <span className="mx-2 text-slate-400">·</span>
              <span className="text-slate-700">{vendorName || "—"}</span>
            </> : <span className="text-slate-400">Fetch an invoice to populate vendor details</span>}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="rts-material-code">Material Code</Label>
          <Input id="rts-material-code" value={materialCode} onChange={(e) => setMaterialCode(e.target.value)} readOnly={lineLocked} data-testid="rts-material-input" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="rts-material-desc">Material Description</Label>
          <Input id="rts-material-desc" value={materialDescription} onChange={(e) => setMaterialDescription(e.target.value)} readOnly={lineLocked} data-testid="rts-material-desc-input" />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rts-grn">GRN ID / Item</Label>
          <div className="flex gap-1.5">
            <Input id="rts-grn" value={grnId} onChange={(e) => setGrnId(e.target.value)} className="flex-1" readOnly={lineLocked} data-testid="rts-grn-input" />
            <Input value={grnItemNumber} onChange={(e) => setGrnItemNumber(e.target.value)} className="w-20" readOnly={lineLocked} placeholder="Item" data-testid="rts-grn-item-input" />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="rts-po">PO Number / Item</Label>
          <div className="flex gap-1.5">
            <Input id="rts-po" value={po} onChange={(e) => setPo(e.target.value)} className="flex-1" readOnly={lineLocked} data-testid="rts-po-input" />
            <Input value={poItem} onChange={(e) => setPoItem(e.target.value)} className="w-20" readOnly={lineLocked} data-testid="rts-po-item-input" />
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="rts-qty">Return Quantity</Label>
          <Input id="rts-qty" type="number" min="0" step="any" value={returnQty} onChange={(e) => setReturnQty(e.target.value)} data-testid="rts-qty-input" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="rts-uom">UOM</Label>
          <Input id="rts-uom" value={uom} onChange={(e) => setUom(e.target.value)} placeholder="EA · KG · MTR" readOnly={lineLocked} data-testid="rts-uom-input" />
        </div>

        <div className="space-y-1">
          <Label htmlFor="rts-site">Site</Label>
          <Input id="rts-site" value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. P2" data-testid="rts-site-input" />
        </div>
        <div className="space-y-1 md:col-span-1">
          <Label htmlFor="rts-notes">Notes</Label>
          <Input id="rts-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="QC batch number, inspector initials, etc." data-testid="rts-notes-input" />
        </div>
      </div>

      {formError && <p role="alert" className="border-l-2 border-red-500 bg-red-50 p-2 text-[12px] text-red-800" data-testid="rts-form-error">{formError}</p>}

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="rts-cancel-button">Cancel</Button>
        <Button
          onClick={submit}
          disabled={submitting}
          className="text-white"
          style={{ backgroundColor: SAP_BLUE }}
          data-testid="rts-submit-button"
        >{submitting ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</> : "Create RTS"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
